<template>
<sidebar-widget
    :start-expanded="true"
    :sections="sections"
    :headers="['Plan', 'Tier']"
>
    <template v-slot:title>Insurance Plan</template>

    <template v-slot:headerbutton>
        <button class="btn btn-outline-primary" @click.prevent.stop="addModalOpen = true"><i class="far fa-plus"></i> Add</button>
    </template>

    <template v-slot:row-details="props">
        <insurance-plan-details
            :is-mutation="false"
            :plan="props.row.plan"
        />

        <button-row>
            <button class="btn btn-outline-primary" @click.prevent="removePlan(props.row.plan.id)"><i class="far fa-trash"></i> Delete</button>

            <button class="btn btn-outline-primary" @click.prevent="editPlan(props.row.plan.id)"><i class="far fa-pen"></i> Edit</button>
        </button-row>
    </template>

    <template v-slot:subrow="props">
        <section v-if="Object.keys(pendingChanges[props.row.plan.id] || []).length" class="ml-n2 mr-n2">
            <table class="table css-table wdiget-table table-fixed" >

                <tbody v-for="pc in pendingChanges[props.row.plan.id]" class="pending-change">
                    <tr>
                        <td class="cw-toggle position-relative pl-1">
                            <a href class="widget-toggle-wrapper collapse-trigger" role="button" :class="{'is-expanded': expandedItems[pc.id]}" @click.prevent="expandedItems[pc.id] = !expandedItems[pc.id]">
                                <collapse-toggle-button />
                            </a>
                        </td>

                        <td class="truncate-wrapper pl-0">
                            <div class="text-italic mb-0">Pending Change effective {{ $filters.date(pc.effective_on) }}</div>

                            <div>{{ pc.summary }}</div>
                        </td>
                    </tr>

                    <tr v-show="expandedItems[pc.id]">
                        <td colspan="2" class="pl-1 border-0">
                            <div class="card card-body item-details mb-2">
                                <section class="item-details-section">
                                    <insurance-plan-details
                                        :is-mutation="true"
                                        :plan="pc.plan"
                                        :diff="pc.diff"
                                    />

                                    <button-row>
                                        <button class="btn btn-outline-primary" @click.prevent="removeMutation(pc.plan.id)"><i class="far fa-trash"></i> Cancel</button>
                                    </button-row>

                                </section>
                            </div>
                        </td>
                    </tr>

                </tbody>

            </table>
        </section>
    </template>

    <template v-slot:extra>
        <insurance-plan-modal
            v-if="addModalOpen || editModalOpen"
            :plan="currentPlan"
            :client="client"
            :employee="employee"
            :future-mutations="planMutations"
            :insurance-plans="insurancePlans"
            @close="currentMutationId = currentPlanId = null; addModalOpen = editModalOpen = false"
            @updated="$emit('updated'); getData()"
        />
    </template>
</sidebar-widget>

</template>

<script>
import SidebarWidget from '@/components/SidebarWidget'
import ButtonRow from '@/components/ButtonRow'
import InsurancePlanModal from './InsurancePlanModal'
import InsurancePlanDetails from './InsurancePlanDetails'
import moment from 'moment'


export default {
    props: ['employee', 'client', ],
    components: {SidebarWidget, ButtonRow, InsurancePlanModal, InsurancePlanDetails, },
    computed: {
        sections() {
            const currentRows = []

            this.employee.insurance_plans.filter(p => {
                return (p.is_active)
            }).forEach(p => {
                currentRows.push({
                    plan: p,
                    cells: [
                        p.insurance_plan.plan_name,
                        p.tier_pretty,
                    ],
                    id: p.id,
                })
            })
            return [
                {title: 'Current', rows: currentRows, startExpanded: true},
            ]
        },
        pendingChanges() {
            const pendingChanges = {}

            const mutations = this.planMutations.filter(m => !m.is_applied && m.is_active)
            mutations.sort((a, b) => a.effective_on.localeCompare(b.effective_on))

            const mutationsByPlan = {}

            mutations.forEach((m, idx) => {
                if (!mutationsByPlan[m.employee_insurance_plan_id]) {
                    mutationsByPlan[m.employee_insurance_plan_id] = []
                }

                mutationsByPlan[m.employee_insurance_plan_id].push(m)
            })


            Object.keys(mutationsByPlan).forEach(planId => {
                const planMutations = mutationsByPlan[planId]

                planMutations.forEach((m, idx) => {
                    const prev = (idx == 0) ? this.employee.insurance_plans.find(p => p.id == m.employee_insurance_plan_id) : mutations[idx - 1]
                    const diff = this.computeDiff(m, prev)

                    if (!pendingChanges[m.employee_insurance_plan_id]) {
                        pendingChanges[m.employee_insurance_plan_id] = []
                    }

                    pendingChanges[m.employee_insurance_plan_id].push({
                        plan: m,
                        diff: diff,
                        effective_on: m.effective_on,
                        summary: this.computeDiffLine(diff),
                        id: m.id,
                    })
                })
            })

            return pendingChanges
        },
        currentPlan() {
            if (this.currentPlanId) {
                return this.employee.insurance_plans.find(p => p.id == this.currentPlanId)
            }
            if (this.currentMutationId) {
                return this.planMutations.find(m => m.id == this.currentMutationId)
            }
        },
    },
    data() {
        return {
            addModalOpen: false,
            editModalOpen: false,
            removeModalOpen: false,
            currentPlanId: null,
            currentMutationId: null,
            planMutations: [],
            insurancePlans: [],

            expandedItems: {},
        }
    },
    mounted() {
        this.getData()
    },
    methods: {
        removeMutation(mutationId) {
            const mutation = this.planMutations.find(m => m.id == mutationId)
            if (!mutation) {return}

            if (confirm('Are you sure?')) {
                this.$store.dispatch('START_LOADING')
                return this.$api.del(`/clients/${this.client.id}/employees/${this.employee.id}/insurance-plan-mutations/${mutation.id}`).then(resp => {
                    this.$store.dispatch('STOP_LOADING')
                    this.$bus.showInfo('Successfully deleted insurance plan information.')
                    this.$emit('updated')
                    this.getData()
                }).catch(errors => {
                    this.$store.dispatch('STOP_LOADING')
                    this.$bus.showError(errors.__all__)
                })
            }
        },
        removePlan(planId) {
            if (confirm('Are you sure?')) {
                this.$store.dispatch('START_LOADING')
                const data = {
                    employee_insurance_plan_id: planId,
                    effective_on: moment().format('YYYY-MM-DD'),
                    active_status: 'inactive',
                }

                return this.$api.post(`/clients/${this.client.id}/employees/${this.employee.id}/insurance-plan-mutations`, data).then(resp => {
                    this.$store.dispatch('STOP_LOADING')
                    this.$bus.showInfo('Successfully deleted insurance plan information.')
                    this.$emit('updated')
                    this.getData()
                }).catch(errors => {
                    this.$store.dispatch('STOP_LOADING')
                    this.$bus.showError(errors.__all__)
                })
            }
        },
        editPlan(planId) {
            const plan = this.employee.insurance_plans.find(p => p.id == planId)
            if (!plan) {return}

            const mutations = this.planMutations.filter(m => m.is_active && (m.employee_insurance_plan_id == plan.id))
            let latestMutation = null

            const sortKey = (m) => {
                return `${m.effective_on}-${m.created_on}-${m.id}`
            }

            for (const m of mutations) {
                if (!latestMutation) {
                    latestMutation = m
                    continue
                }

                if (sortKey(m) >= sortKey(latestMutation)) {
                    latestMutation = m
                }
            }

            if (latestMutation) {
                this.currentMutationId = latestMutation.id
            }
            else {
                this.currentPlanId = plan.id
            }

            this.editModalOpen = true
        },
        getData(silent) {
            if (!silent) { this.$store.dispatch('START_LOADING') }

            const promises = []
            promises.push(this.$api.get(`/clients/${this.client.id}/employees/${this.employee.id}/insurance-plan-mutations`).then(resp => {
                this.planMutations = resp
            }))

            promises.push(this.$api.get(`/clients/${this.client.id}/settings/insurance-plans`).then(resp => {
                this.insurancePlans = resp.filter(p => p.company_id == this.employee.company_id)
            }))

            Promise.all(promises).finally(() => {
                this.$store.dispatch('STOP_LOADING')
            })
        },
        computeDiff(cur, old) {
            const fields = [
                'tier',
                'employee_monthly_rate',
                'employer_monthly_rate',
            ]

            const diff = {}
            for (const field of fields) {
                const curVal = cur[field] ? cur[field] : 'N/A'
                const oldVal = old[field] ? old[field] : 'N/A'

                if (curVal != oldVal) {
                    diff[field] = {old: oldVal, cur: curVal}
                }
            }

            return diff
        },
        computeDiffLine(diff) {
            const fieldLabels = {
                'tier': 'Tier',
                'employee_monthly_rate': 'Employee Cost',
                'employer_monthly_rate': 'Employer Cost',
            }

            if (Object.keys(diff).length == 0) {
                return 'No changes to be applied.' // This shouldn't happen
            }

            if (Object.keys(diff).length == 1) {
                const old = Object.values(diff)[0].old
                const cur = Object.values(diff)[0].cur

                if (diff.tier) {
                    return `Tier changed from ${old} to ${cur}`
                }
                else if (diff.employee_monthly_rate) {
                    return `Employee cost changed from ${this.$filters.dollars(old)} to ${this.$filters.dollars(cur)}`
                }
                else if (diff.employer_monthly_rate) {
                    return `Employer Cost changed from ${this.$filters.dollars(old)} to ${this.$filters.dollars(cur)}`
                }
            }

            const labels = []
            for (const field of Object.keys(diff)) {
                const label = fieldLabels[field]
                labels.push(label)
            }

            return `${labels.join(', ')} have been changed`
        },
    },
}
</script>

<style scoped lang="scss">
.pending-change {
    background-color: #e8f4f9;
    color: #0c5460;
    border-radius: 0.25rem;
    border-left: 8px solid #1c69a3;
}
</style>
